import React, {useContext, useEffect, useRef, useState} from 'react';
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {Toast} from "primereact/toast";
import {usePagination} from "../../../../../../layout/hooks/usePagination";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import {webInfo} from "../../../../../../utils/webInfo";
import callToast, {
    handleChangeData,
    mergeWithSelected,
    postData,
    validateFormFields
} from "../../../../../../utils/helper";
import {Button} from "primereact/button";
import PageHeading from "../../../../../../Components/PageHeading";
import {DataTable, DataTableRowToggleEvent} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import Label from "../../../../../../Components/Label";
import {InputText} from "primereact/inputtext";
import {ParkingSlot} from "../../../../../../types/demo";
import {InputNumber} from "primereact/inputnumber";
import {Dropdown} from "primereact/dropdown";
import mapboxgl from "mapbox-gl";
import {InputTextarea} from "primereact/inputtextarea";


const ParkingSlots = () => {
    const initialState: ParkingSlot = {
        slot_code: '',
        vehicle_parking_floor_id: null,
        vehicle_parking_slot_type_id: null,
        height: null,
        width: null,
        description: "",
        status: null,
        vehicle_parking_campus_id: null
        // vehicle_parking_campus_id: {id:13, name:"test"}
    };

    const {accessToken} = useContext(LayoutContext)
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<ParkingSlot>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [expandedRows, setExpandedRows] = useState<any | undefined>(undefined);

    const statuses = [
        {id: 1, name: 'Available'},
        {id: 2, name: 'Reserved'},
        {id: 3, name: 'Booked'},
    ];

    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);

    const [parkingCampusNameFilter, setParkingCampusNameFilter] = useState<string>('');
    const [parkingFloorNameFilter, setParkingFloorNameFilter] = useState<string>('');
    const [parkingSlotTypeNameFilter, setParkingSlotTypeNameFilter] = useState<string>('');

    const [parkingCampusOptions, setParkingCampusOptions] = useState<{ id: number, name: string }[]>([]);
    const [parkingFloorOptions, setParkingFloorOptions] = useState<{ id: number, floor_name: string, floor_level:number }[]>([]);
    const [parkingSlotTypeOptions, setParkingSlotTypeOptions] = useState<{ id: number, name: string }[]>([]);


    const [mapModal, setMapModal] = useState<boolean>(false);
    const [locationData, setLocationData] = useState<{
        latitude: string | number,
        longitude: string | number,
        name: string,
        address: string
    }>({
        latitude: '90.4125', longitude: '23.8103', name: '', address: ''
    });


    const showMapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);


    const initMap = () => {
        // Only use showMapContainerRef for view mode
        const targetContainer = showMapContainerRef.current;
        if (!targetContainer) return;

        mapboxgl.accessToken = webInfo?.mapBoxToken;

        const rawLat = parseFloat(String(locationData.latitude));
        const rawLng = parseFloat(String(locationData.longitude));
        const isValidLat = !isNaN(rawLat) && rawLat >= -90 && rawLat <= 90;
        const isValidLng = !isNaN(rawLng) && rawLng >= -180 && rawLng <= 180;

        const finalLng = isValidLng ? rawLng : 90.4125;
        const finalLat = isValidLat ? rawLat : 23.8103;

        mapRef.current = new mapboxgl.Map({
            container: targetContainer,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [finalLng, finalLat],
            zoom: isValidLat && isValidLng ? 15 : 11
        });

        // Initialize Marker (not draggable in view mode)
        markerRef.current = new mapboxgl.Marker({
            draggable: false,
            color: "#ef4444"
        })
            .setLngLat([finalLng, finalLat])
            .addTo(mapRef.current);
    };
    const destroyMap = () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
            markerRef.current = null;
        }
    };

    // pagination
    const {page, limitData, handlePageChange} = usePagination();

    // endpoint
    const mainTitle = "Parking Slots"
    const tableDataEndpointUrl = `/vehicle-parking-slots?page=${page} `
    const submitFormEndpointUrl = updatedId ? `/vehicle-parking-slots/${updatedId}` : "/vehicle-parking-slots";

    // fetch data
    const {
        data: tableDataList,
        totalData, loading
    } = useFetch(tableDataEndpointUrl, [refresh])

    // fetch dorpdown data
    // parking campus
    const {data: parkingCampusRawData} = useFetch(
        `/vehicle-parking-campuses?page=1&per_page=10${parkingCampusNameFilter ? `&name=${parkingCampusNameFilter}` : ''}`,
        [parkingCampusNameFilter]
    );
    useEffect(() => {
        if (parkingCampusRawData) {
            const modifiedData = parkingCampusRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name
            }));
            setParkingCampusOptions(mergeWithSelected(modifiedData, formData?.vehicle_parking_campus_id));
        }
    }, [parkingCampusRawData, formData?.vehicle_parking_campus_id]);

    // parking floor
    const {
        data: parkingFloorRawData

    } = useFetch(`/vehicle-parking-campuses/${formData.vehicle_parking_campus_id?.id}/floors?page=1&per_page=10${parkingFloorNameFilter ? `&floor_name=${parkingFloorNameFilter}` : ''}`, [parkingFloorNameFilter, formData?.vehicle_parking_campus_id], true, formData?.vehicle_parking_campus_id !== null);

    useEffect(() => {
        if (parkingFloorRawData) {
            const modifiedData = parkingFloorRawData?.map((item: any) => ({
                id: item?.id,
                floor_name: item?.floor_name,
                floor_level: item?.floor_level
            }));
            setParkingFloorOptions(mergeWithSelected(modifiedData, formData?.vehicle_parking_floor_id));
        }
    }, [parkingFloorRawData, formData?.vehicle_parking_floor_id]);


    // parking slot type
    const {data: parkingSlotTypeRawData} = useFetch(
        `/vehicle-parking-slot-types?page=1&per_page=10${parkingSlotTypeNameFilter ? `&name=${parkingSlotTypeNameFilter}` : ''}`,
        [parkingSlotTypeNameFilter]
    );
    useEffect(() => {
        if (parkingSlotTypeRawData) {
            const modifiedData = parkingSlotTypeRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name
            }));
            setParkingSlotTypeOptions(mergeWithSelected(modifiedData, formData?.vehicle_parking_slot_type_id));
        }
    }, [parkingSlotTypeRawData, formData?.vehicle_parking_slot_type_id]);


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [
            {field: 'slot_code', label: 'Slot Code'},
            {field: 'status', label: 'Status'},
            {field: 'vehicle_parking_campus_id', label: 'Parking Campus'},
            {field: 'vehicle_parking_floor_id', label: 'Parking Floor'},
            {field: 'vehicle_parking_slot_type_id', label: 'Slot Type'},
            {field: 'height', label: 'Height'},
            {field: 'width', label: 'Width'},
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            slot_code: formData.slot_code.replace(/\s+/g, ' ').trim(),
            status: formData.status?.id,
            vehicle_parking_campus_id: formData.vehicle_parking_campus_id?.id,
            vehicle_parking_floor_id: formData.vehicle_parking_floor_id?.id,
            vehicle_parking_slot_type_id: formData.vehicle_parking_slot_type_id?.id,
            height: formData.height,
            width: formData.width,
            description: formData.description.replace(/\s+/g, ' ').trim(),
        };


        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, postData("PUT", accessToken, payload));


            if (response.ok) {
                callToast(toast, true, `Vehicle ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState)
                setModal(false)
                setUpdatedId(null)
                setParkingSlotTypeNameFilter('')
                setParkingFloorNameFilter('')
                setParkingCampusNameFilter('')
                setRefresh(!refresh);
                return;
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }

        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }

    }

    // row edit template
    const actionTemplate = (rowData: any) => {

        return (
            <div className={'flex align-items-center justify-content-end'}>
                <Button icon={'pi pi-pencil'} className={'border-rounded w-2rem h-2rem'} onClick={() => {
                    const updatedStatus =
                        rowData.status === 1
                            ? { id: 1, name: 'Available' }
                            : rowData.status === 2
                                ? { id: 2, name: 'Reserved' }
                                : { id: 3, name: 'Booked' };
                    setModal(true)
                    setUpdatedId(rowData?.id)
                    setParkingSlotTypeNameFilter('')
                    setParkingFloorNameFilter('')
                    setParkingCampusNameFilter('')
                    setFormData({
                        slot_code: rowData.slot_code,
                        status: updatedStatus,
                        vehicle_parking_campus_id: rowData?.parking_floor?.campus ? {id: rowData?.parking_floor?.campus?.id, name: rowData?.parking_floor?.campus?.name} : null,
                        vehicle_parking_floor_id: rowData?.parking_floor ? {id: rowData?.parking_floor?.id, floor_name: rowData?.parking_floor?.floor_name, floor_level: rowData?.parking_floor?.floor_level} : null,
                        vehicle_parking_slot_type_id: rowData?.slot_type ? {id: rowData?.slot_type?.id, name: rowData?.slot_type?.name} : null,
                        height: rowData.height,
                        width: rowData.width,
                        description: rowData.description,
                    })
                }} outlined/>
            </div>
        )
    }

    const parkingLocationBodyTemplate = (rowData: any) => {
        const campusLocationData = rowData?.parking_floor?.campus;
        const hasLatLng = campusLocationData?.latitude && campusLocationData?.longitude;

        return (
            <div className="flex flex-column gap-1">
            <span className="font-semibold text-900 text-sm">
                {rowData.parking_floor?.floor_name || '-'}
            </span>
                <span className="text-700 text-xs">
                Level {rowData.parking_floor?.floor_level || '-'}
            </span>
                <span className="text-700 text-xs flex align-items-center">
                    {hasLatLng && (
                        <i
                            className="pi pi-map-marker mr-2 cursor-pointer"
                            onClick={() => {

                                setMapModal(true);
                                setLocationData({
                                    latitude: campusLocationData?.latitude,
                                    longitude: campusLocationData?.longitude,
                                    name: campusLocationData?.name,
                                    address: campusLocationData?.address,
                                })

                            }}
                            style={{color: '#ef4444'}}/>
                    )}
                    {rowData.parking_floor?.campus?.name || '-'}

            </span>
            </div>
        );
    };




    function message(msg: string, status: string) {
        return <span className={`custom-badge ${status}`}>{msg}</span>;
    }

    const statusDropdownBodyTemplate = (rowData: any) => {
        return (
            <span className={'w-full'}>
        {rowData.status === 1
            ? message("Available", "approved")
            : rowData.status === 2
                ? message("Reserved", "pending")
                : rowData.status === 3
                    ? message("Booked", "cancelled")
                    : message("Unknown", "neutral")}
    </span>
        );

    }

    const allowExpansion = (rowData: any) => {
        return rowData?.description?.length > 0;
    };

    const rowExpansionTemplate = (data: any) => {
        console.log('data row expansion: ', data);
        return (
            <div className="p-4  rounded-md shadow-sm">
                <h6 className="text-lg font-semibold mb-2 text-gray-700 flex items-center gap-2">
                    Description
                </h6>
                <p className="text-gray-600 text-base">{data.description}</p>
            </div>
        );
    };


    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <PageHeading title={mainTitle}
                         isCreate={true}
                         createFunction={() => setModal(true)}
                         back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{minWidth: '50rem'}}
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}


                expandedRows={expandedRows}
                onRowToggle={(e: DataTableRowToggleEvent) => setExpandedRows(e?.data)}
                rowExpansionTemplate={rowExpansionTemplate}
            >
                <Column expander={allowExpansion} style={{width: '3rem'}}/>
                <Column
                    field="slot_code"
                    header="Slot Code"
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap"}/>
                <Column
                    field="parking_floor"
                    header="Parking Location"
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap"}
                    body={parkingLocationBodyTemplate}
                />
                <Column
                    field="slot_type.name"
                    header="Slot Type"
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap"}
                />
                <Column
                    field="height"
                    header="Area"
                    style={{width: '20%'}}
                    body={(rowData: any) => `${rowData.height}m x ${rowData.width}m`}
                    headerClassName={"white-space-nowrap"}
                />
                <Column
                    field="status"
                    header="Status"
                    style={{width: '20%'}}
                    body={statusDropdownBodyTemplate}
                />


                <Column header={"Action"} rowEditor={true}
                        bodyStyle={{textAlign: 'center'}}
                        body={actionTemplate}
                        headerClassName={"flex justify-content-end"}
                />

            </DataTable>

            <Dialog visible={modal}
                    style={{width: "50rem"}}
                    headerClassName="text-800 font-semibold text-xl"
                    header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                    breakpoints={{"960px": "75vw", "640px": "90vw"}}
                    onHide={() => {
                        setUpdatedId(null)
                        setModal(false)
                        setFormData(initialState)
                        setParkingSlotTypeNameFilter('')
                        setParkingFloorNameFilter('')
                        setParkingCampusNameFilter('')
                    }}
            >
                <div>
                    <form className={''}
                          onSubmit={handleSubmit}
                    >
                        <div className="grid ">
                            <div className="col-12  md:col-6">
                                <Label label={"Slot Code"} required={!updatedId}/>
                                <InputText
                                    id="slot_code"
                                    value={formData.slot_code}
                                    size={100}
                                    name={"slot_code"}
                                    onChange={(e) => handleChangeData('slot_code', e.target.value, formData, setFormData)}
                                    placeholder={"Enter slot code"}
                                    className={`w-full`}
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <Label label={"Status"} required={!updatedId}/>
                                <Dropdown
                                    options={statuses}
                                    id={"status"}
                                    name={"status"}
                                    optionLabel="name"
                                    placeholder="Select status"
                                    value={formData?.status}
                                    onChange={(e) => handleChangeData('status', e.value, formData, setFormData)}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="grid mt-2">
                            <div className="col-12  md:col-4">
                                <Label label={"Parking Campus"} required={!updatedId}/>
                                <Dropdown
                                    options={parkingCampusOptions? parkingCampusOptions : []}
                                    optionLabel="name"
                                    id="vehicle_parking_campus_id"
                                    name={`vehicle_parking_campus_id`}
                                    className={`w-full`}
                                    value={formData?.vehicle_parking_campus_id}
                                    onChange={(e) => handleChangeData('vehicle_parking_campus_id', e.value, formData, setFormData)}
                                    placeholder="Select campus"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setParkingCampusNameFilter(e.filter)
                                        }, 500);

                                        if (parkingCampusRawData) {
                                            const modifiedData = parkingCampusRawData.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name
                                            }));
                                            setParkingCampusOptions(modifiedData);
                                        }
                                    }}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                            <div className="col-12  md:col-4">
                                <Label label={"Parking floor"} required={!updatedId}/>
                                <Dropdown
                                    options={parkingFloorOptions ? parkingFloorOptions : []}
                                    id="vehicle_parking_floor_id"
                                    name={`vehicle_parking_floor_id`}
                                    optionLabel="floor_name"
                                    className={`w-full`}
                                    value={formData?.vehicle_parking_floor_id}
                                    onChange={(e) => handleChangeData('vehicle_parking_floor_id', e.value, formData, setFormData)}
                                    placeholder="Select floor"
                                    disabled={formData?.vehicle_parking_campus_id === null}
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setParkingFloorNameFilter(e.filter)
                                        }, 500);

                                        if (parkingFloorRawData) {
                                            const modifiedData = parkingFloorRawData.map((item: any) => ({
                                                id: item?.id,
                                                floor_name: item?.floor_name,
                                                floor_level: item?.floor_level,
                                            }));
                                            setParkingFloorOptions(modifiedData);
                                        }
                                    }}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                            <div className="col-12  md:col-4">
                                <Label label={"Parking slot type"} required={!updatedId}/>
                                <Dropdown
                                    options={parkingSlotTypeOptions ? parkingSlotTypeOptions : []}
                                    id="vehicle_parking_slot_type_id"
                                    name={`vehicle_parking_slot_type_id`}
                                    optionLabel="name"
                                    className={`w-full`}
                                    value={formData?.vehicle_parking_slot_type_id}
                                    onChange={(e) => handleChangeData('vehicle_parking_slot_type_id', e.value, formData, setFormData)}
                                    placeholder="Select slot type"
                                    // disabled={formData?.vehicle_parking_campus_id === null && formData?.vehicle_parking_floor_id === null}
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setParkingSlotTypeNameFilter(e.filter)
                                        }, 500);

                                        if (parkingSlotTypeRawData) {
                                            const modifiedData = parkingSlotTypeRawData.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name
                                            }));
                                            setParkingSlotTypeOptions(modifiedData);
                                        }
                                    }}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                        </div>
                        <div className="grid mt-2">
                            <div className="col-12  md:col-4">
                                <Label label={"Height"} required={!updatedId}/>
                                <InputNumber
                                    id="height"
                                    value={formData.height}
                                    name={"height"}
                                    mode="decimal"
                                    minFractionDigits={1}
                                    maxFractionDigits={2}
                                    // maxFractionDigits={2}
                                    suffix={" m"}
                                    onValueChange={(e) => handleChangeData('height', e.value, formData, setFormData)}
                                    placeholder={"Enter height"}
                                    className={`w-full p-fluid`}
                                />
                            </div>
                            <div className="col-12  md:col-4">
                                <Label label={"Width"} required={!updatedId}/>
                                <InputNumber
                                    id="width"
                                    value={formData.width}
                                    name={"width"}
                                    mode="decimal"
                                    minFractionDigits={1}
                                    maxFractionDigits={2}
                                    // maxFractionDigits={2}
                                    suffix={" m"}
                                    onValueChange={(e) => handleChangeData('width', e.value, formData, setFormData)}
                                    placeholder={"Enter width"}
                                    className={`w-full p-fluid`}
                                />
                            </div>
                        </div>
                        <div className="grid mt-2">
                            <div className="col-12 md:col-10">
                                <Label label={"Description"}/>
                                <InputTextarea
                                    id="description"
                                    value={formData.description}
                                    name={"description"}
                                    className={`w-full`}
                                    onChange={(e) => handleChangeData('description', e.target.value, formData, setFormData)}
                                    placeholder={"Enter description"}
                                />
                            </div>

                        </div>


                        <div className="flex gap-2 mt-2">
                            <Button
                                type="submit"
                                label={updatedId ? 'Update' : "Create"}
                                className="p-button-primary"
                                size={'small'}
                                loading={isSubmitting}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>


            {/* VIEW MAP MODAL */}
            <Dialog
                visible={mapModal}
                style={{width: "50rem"}}
                header={"Location Details"}
                onHide={() => {
                setMapModal(false);
                setLocationData({
                    latitude: '90.4125', longitude: '23.8103', name: '', address: ''
                });
                destroyMap();
            }}
                onShow={initMap}
            >
                <div className="grid">
                    <div className="col-12">
                        <div className="surface-100 p-3 border-round mb-3">
                            <div className="font-bold text-lg mb-1">{locationData.name}</div>
                            <div className="text-600 mb-2"><i className="pi pi-map-marker mr-1"/>{locationData.address}
                            </div>
                        </div>
                        <div ref={showMapContainerRef} style={{
                            height: '400px',
                            width: '100%',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                        }}/>
                    </div>
                </div>
            </Dialog>

        </div>
    );
};

export default ParkingSlots;

